import { now, fmt, todayStr, localDateStr } from "./dateHelpers";
import { goalOn } from "./habitWeeklyGoal";
import { addDaysToDateStr, dateTimeToDate } from "./taskLogic";
import { DEFAULT_ALARM_SOUND, NOTIF_GRACE_MS } from "../constants/misc";
import { isIntervalDueOn, isIntervalDueFrom } from "./habitInterval";

export function isHabitDueOn(habit, dateStr) {
  // A specific-days habit whose days were changed as a new goal is judged by
  // the days that applied on this date (same object back if never changed).
  habit = goalOn(habit, dateStr);
  // A habit "reschedule" (Skip modal, HabitsTab.jsx) records the move
  // directly on the habit itself, since habits have no separate per-
  // occurrence override store. Checked first so the picked day is due
  // regardless of the habit's normal pattern, and the day it was skipped
  // FROM never is. Mirrored in lib/habit-reminder-logic.js — keep in sync.
  if (habit.movedOccurrences && habit.movedOccurrences.length) {
    if (habit.movedOccurrences.some(m => m.from === dateStr)) return false;
    if (habit.movedOccurrences.some(m => m.to === dateStr)) return true;
  }
  // Excluded dates (holidays, birthdays): never due, for every caller. After
  // the move check, so a day the user explicitly moved an occurrence TO wins.
  if (isHabitExcludedOn(habit, dateStr)) return false;
  const d = new Date(dateStr + "T00:00:00");
  if (habit.frequencyType === "daily") return true;
  // Every N days / weeks / months, counted from the start date (habitInterval.js).
  if (habit.frequencyType === "interval") return isIntervalDueOn(habit, dateStr);
  // "Once" — custom to-dos only (never exposed for real habits): due only on
  // the day it was created, so it shows up today and never again afterward.
  // Ids for these are plain "${Date.now()}" strings, so they double as a
  // creation timestamp with no extra field needed.
  if (habit.frequencyType === "once") {
    const created = /^\d+$/.test(String(habit.id)) ? localDateStr(new Date(Number(habit.id)).toISOString()) : dateStr;
    return dateStr === created;
  }
  // Bug fix: "count" habits (X times/week) have no specific due date —
  // returning true every day caused them to appear as "missed" daily.
  // Callers that want to show logged count-habit days check habitLogs directly.
  if (habit.frequencyType === "count") return false;
  if (habit.frequencyType === "yearly") {
    if ((habit.yearlyMode || "count") === "count") return false; // no specific due dates
    const md = `${d.getMonth() + 1}-${d.getDate()}`;
    return (habit.yearDates || []).includes(md);
  }
  if (habit.frequencyType === "monthly") {
    if (habit.monthlyMode === "count") return false; // no specific due dates, like weekly count
    return habit.monthDates?.includes(d.getDate());
  }
  if (habit.frequencyType === "specific" && habit.dayLabels === "named") {
    const dow = d.getDay();
    const mapped = [6, 0, 1, 2, 3, 4, 5];
    return habit.days?.includes(mapped[dow]);
  }
  return true;
}

// ─── EXCLUDED DATES ─────────────────────────────────────────────────────────
// `excludedDates`: one-off "YYYY-MM-DD" days a habit is off (Christmas, a
// birthday), picked in the habit form. On such a day the habit is simply not
// due: hidden from the daily list, never a miss, no reminder, and left out of
// both sides of every completion score (not a free win either).
//
// Only habits with fixed due days carry them. Counted habits ("X times a
// week/month/year") have no day to exclude, so a leftover list from a type
// switch is ignored rather than hiding the habit. Restricted habits (not-to-do
// / limited) are judged day by day, so they do take part.
//
// Mirrored in lib/habit-reminder-logic.js — the server push must agree with
// the phone's local alarm about which days are off (keep the two in sync).
export function hasExcludableDates(habit) {
  if (!habit) return false;
  if (habit.habitType === "nottodo" || habit.habitType === "limited") return true;
  return habit.frequencyType !== "once" && !isCountFrequency(habit);
}

export function isHabitExcludedOn(habit, dateStr) {
  const list = habit && habit.excludedDates;
  if (!Array.isArray(list) || !list.length || !dateStr) return false;
  return hasExcludableDates(habit) && list.includes(dateStr);
}

// Whether a habit would be due on dateStr if it weren't excluded — so an
// excluded date that falls on a day off anyway (Christmas on a Saturday for a
// Mon–Fri habit) doesn't lower a target that never counted it.
function dueIgnoringExclusion(habit, dateStr) {
  if (habit.habitType === "nottodo") return true;
  if (habit.habitType === "limited") {
    if (habit.allowanceMode) return true; // budget habits count every day
    return isLimitedRestrictedOn(habit, dateStr);
  }
  return !!isHabitDueOn({ ...habit, excludedDates: undefined }, dateStr);
}

// How many excluded dates in [fromStr, toStr] (inclusive) would otherwise have
// been due. Every Target Completion target subtracts this, so a week holding
// Christmas reads x/6 for a daily habit and a full week is still reachable.
// Dates before the habit's start are ignored, like everything else.
export function excludedDueDays(habit, fromStr, toStr) {
  if (!hasExcludableDates(habit) || !Array.isArray(habit.excludedDates)) return 0;
  const start = habit.habitStartDate || localDateStr(habit.createdAt) || "";
  let n = 0;
  for (const d of new Set(habit.excludedDates)) {
    if (typeof d !== "string" || d < fromStr || d > toStr || d < start) continue;
    if (dueIgnoringExclusion(habit, d)) n++;
  }
  return n;
}

// Adds/removes one excluded date, keeping the list sorted and duplicate-free.
// An empty list is stored as [] (Firestore drops undefined).
export function toggleExcludedDate(list, dateStr) {
  const cur = Array.isArray(list) ? list : [];
  if (!dateStr) return cur;
  if (cur.includes(dateStr)) return cur.filter(d => d !== dateStr);
  return [...cur, dateStr].sort();
}

export function isHabitDoneOn(habit, habitLogs, key, habitSkips = []) {
  const slipped = habit.relapseLog?.some(r => r.date === key);
  const skipped = habitSkips.some(s => s.habitId === habit.id && s.skippedDate === key);
  if (habit.habitType === "nottodo") {
    // Auto-complete: clean unless slipped or skipped
    return !slipped && !skipped;
  } else if (habit.habitType === "limited") {
    // Auto-complete: clean unless slipped or skipped
    // (over-limit log also counts as a slip implicitly via relapseLog)
    return !slipped && !skipped;
  } else {
    const logVal = habitLogs[key]?.[habit.id];
    const target = goalOn(habit, key).dailyCount || 1;
    return typeof logVal === "number" ? logVal >= target : !!logVal;
  }
}

// ─── LIMITED-HABIT DAY SEMANTICS ────────────────────────────────────────────
// "limited" restricted habits let the user pick the days the habit is NOT
// allowed (i.e. the days to keep clean). Historically the picker stored the
// *allowed* days in `allowedDays`; we now store the *off* (not-allowed) days in
// `limitedOffDays`. This accessor migrates legacy habits lazily by inverting the
// old allowedDays set, so unedited habits keep behaving as intended.
// Returns Mon=0…Sun=6 indices. Empty array = restricted every day.
export function limitedOffDays(h) {
  if (Array.isArray(h.limitedOffDays)) return h.limitedOffDays;
  if (Array.isArray(h.allowedDays) && h.allowedDays.length > 0) {
    return [0, 1, 2, 3, 4, 5, 6].filter(d => !h.allowedDays.includes(d));
  }
  return []; // blank → callers treat as "restricted every day"
}

// Whether dateStr is a NOT-allowed (restricted) day for a "specific days"
// limited habit: its weekday is in limitedOffDays (none = every day), or, with
// `limitedInterval` (Jade, 2026-10-17), it falls on the habit's "every N days /
// weeks / months" rhythm, counted from its start date like a To Do interval.
// Budget habits (allowanceMode set) don't use this.
export function isLimitedRestrictedOn(h, dateStr) {
  if (h.limitedInterval) return isIntervalDueFrom(h, h.habitStartDate || h.intervalStart || dateStr, dateStr);
  const off = limitedOffDays(h);
  return !off.length || off.includes((new Date(dateStr + "T00:00:00").getDay() + 6) % 7);
}

// A yearly *counted* habit is due `yearlyCount` times within 365 days of its
// start — the deadline is start + 365 days (day-based, not calendar-based).
export function habitYearlyDueDate(h) {
  const startStr = h.habitStartDate || localDateStr(h.createdAt) || todayStr();
  const startMs = new Date(startStr + "T00:00:00").getTime();
  return fmt(new Date(startMs + 365 * 86400000));
}

// Only yearly-COUNTED habits can be hidden from the daily list (they have no
// per-day due). They default to hidden and live in the "Long-term habits"
// drawer; the "Show on daily list" toggle opts them back in. Every other habit
// type ignores this entirely — its daily-list visibility depends on the day.
// Restricted habits (limited / not-to-do) are never counted: they can carry a
// leftover frequencyType from when the form was on a To Do habit, and that must
// not hide them from the daily list or offer a toggle they can't use.
export function isHabitCountedYearly(h) {
  if (h.habitType === "limited" || h.habitType === "nottodo") return false;
  return h.frequencyType === "yearly" && (h.yearlyMode || "count") === "count";
}
export function isHabitHiddenFromDaily(h) {
  if (!isHabitCountedYearly(h)) return false;
  return h.showOnDailyList !== true; // default hidden unless opted in
}

// Returns true if the habit is currently paused (pausedUntil is set and >= today)
export function isHabitPaused(habit) {
  if (!habit.pausedUntil) return false;
  return habit.pausedUntil >= todayStr();
}

// Shared start/end-date + pause guard — the single source of truth for whether
// a habit (of ANY type) is within its active date range today. Both the To Do
// list and the Restricted/Boundary list must use this exact check so a habit's
// start/end date behaves identically no matter its habitType.
export function isWithinHabitRange(habit, t = todayStr()) {
  if (habit.habitStartDate && habit.habitStartDate > t) return false;
  if (habit.endDate && habit.endDate < t) return false; // past end date — hide silently
  if (isHabitPaused(habit)) return false; // paused — hide from daily list
  return true;
}

export function isHabitDueToday(habit) {
  if (!isWithinHabitRange(habit)) return false;
  if (isHabitExcludedOn(habit, todayStr())) return false; // a day off (holiday, birthday…)
  // ── Restricted habits: visibility depends on restriction type ──
  if (habit.habitType === "nottodo") return true; // full stop = relevant every day
  if (habit.habitType === "limited") {
    // visible on NOT-allowed (restricted) days only (if none set = every day)
    return isLimitedRestrictedOn(habit, todayStr());
  }
  // ── Todo habits ──
  if (habit.frequencyType === "daily") return true;
  if (habit.frequencyType === "interval") return isHabitDueOn(habit, todayStr());
  if (habit.frequencyType === "count") return true;
  if (habit.frequencyType === "yearly") {
    // Counted yearly habits have no per-day due — they live in the hidden
    // drawer, not the daily list. Specific-date yearly habits show on match.
    if ((habit.yearlyMode || "count") === "count") return false;
    const md = `${now().getMonth() + 1}-${now().getDate()}`;
    return (habit.yearDates || []).includes(md);
  }
  if (habit.frequencyType === "monthly") {
    if (habit.monthlyMode === "count") return true; // always visible, like weekly count
    return true;
  }
  if (habit.frequencyType === "specific") {
    if (habit.dayLabels === "named") {
      const dow = now().getDay();
      const mapped = [6, 0, 1, 2, 3, 4, 5];
      return habit.days?.includes(mapped[dow]);
    }
    return true;
  }
  return true;
}



export function getStreak(habit, habitLogs) {
  // Bug fix: skip days where the habit isn't due (e.g. a Mon/Wed/Fri habit
  // should not have its streak broken by Tuesday or Saturday).
  // Only break the streak when a *due* day was missed.
  // Paused days are also skipped — they don't count for or against the streak.
  let streak = 0;
  const d = new Date();
  for (let i = 0; i < 365; i++) {
    const key = fmt(d);
    // Skip paused days — invisible to the streak counter
    if (habit.pausedUntil && key <= habit.pausedUntil) {
      d.setDate(d.getDate() - 1);
      continue;
    }
    const due = isHabitDueOn(habit, key) && habit.frequencyType !== "count";
    if (due) {
      const logVal = habitLogs[key]?.[habit.id];
      const target = goalOn(habit, key).dailyCount || 1;
      const fullyDone = typeof logVal === "number" ? logVal >= target : !!logVal;
      if (fullyDone) {
        streak++;
      } else {
        // Due day not fully completed — streak is broken (allow today to still be pending)
        if (i > 0) break;
      }
    }
    // Non-due day: just skip, don't touch streak
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

// ─── CONSISTENCY SCORE ────────────────────────────────────────────────────────

// Check if a habit was completed on a given day key
// isHabitDoneOn lives in ./utils/habitLogic (imported above).

// Check if a habit is due on a given day key
// ── Counted habits and reminders ────────────────────────────────────────────
// "Counted" habits are the three shapes with no specific due dates: X times a
// week (frequencyType "count"), X times a month (monthly + monthlyMode
// "count") and X times a year (yearly + yearlyMode "count"). isHabitDueOn
// returns false for all of them, which is correct everywhere else — they can't
// be "missed" on a particular day — but it also meant they could never carry a
// reminder at all.
export function isCountFrequency(habit) {
  if (!habit) return false;
  if (habit.frequencyType === "count") return true;
  if (habit.frequencyType === "monthly") return habit.monthlyMode === "count";
  if (habit.frequencyType === "yearly") return (habit.yearlyMode || "count") === "count";
  return false;
}

// Whether a habit's reminder should fire on a given day. Same as isHabitDueOn
// for everything with real due dates.
//
// A counted habit has none, so "every day it's due" is meaningless — it would
// either never ring or ring forever. Instead its reminder is a ONE-SHOT armed
// for the day it was set (`reminderDate`, stamped by the habit form) and for
// that day only. A time already past when it's set simply never fires, which
// is the intended behaviour: pick a later time to arm it. Once the day is
// over the reminder is spent, and clearSpentCountReminders below empties it so
// nothing stale sits in the form implying an alarm that isn't coming.
//
// If the habit starts AFTER the day it was set, the armed day is the start
// date instead (see countedReminderArmedDate). Keep in lockstep with
// lib/habit-reminder-logic.js.
export function isHabitDueForReminderOn(habit, dateStr) {
  if (isCountFrequency(habit)) return countedReminderArmedDate(habit) === dateStr;
  return isHabitDueOn(habit, dateStr);
}

// The one day a counted habit's one-shot reminder is armed for: the later of
// `reminderDate` (when it was set) and `habitStartDate`. The collectors gate on
// the habit's range first, so a reminder set before the start date could never
// ring on the day it was stamped, and would be cleared before the habit began.
// Computed rather than re-stamped, so moving the start date later just works.
// No `reminderDate` means nothing is armed. Plain YYYY-MM-DD string compares.
export function countedReminderArmedDate(habit) {
  if (!habit || !habit.reminderDate) return "";
  const start = habit.habitStartDate || "";
  return start > habit.reminderDate ? start : habit.reminderDate;
}

// Empties the reminder on any counted habit whose armed day has passed. Run on
// every load and on every remote sync (see App.jsx), rather than at ring time —
// nothing is running then, and the native side can't write back to habit data.
export function clearSpentCountReminders(habits, today = todayStr()) {
  if (!Array.isArray(habits)) return habits;
  return habits.map(h => {
    if (!h || !h.reminderTime || !isCountFrequency(h)) return h;
    const armed = countedReminderArmedDate(h);
    if (armed && armed >= today) return h;
    return { ...h, reminderTime: "", reminderDate: "" };
  });
}

export function isHabitDueOnKey(habit, key) {
  if (habit.habitType === "nottodo" || habit.habitType === "limited") return !isHabitExcludedOn(habit, key);
  if (habit.frequencyType === "count") return true;
  return isHabitDueOn(habit, key);
}

// ── ROLLING SCORING-PERIOD HELPERS ───────────────────────────────────────────

// 28 days, not 30, because 28 is a whole number of weeks and 30 is not.
// With a 30-day period, 30 % 7 === 2, so every period started two weekdays
// later than the last (Thu → Sat → Mon → Wed…) and the "anchored to your start
// date" idea never actually held. Worse, the DENOMINATOR wobbled: the same
// Mon/Wed/Fri habit had 13 due days in one period and 12 in the next, so
// identical behaviour scored differently depending on which period you were
// in. At 28 the period always starts on the habit's own start weekday and
// always contains exactly four of each weekday — and a weekly target divides
// evenly (4×/week × 4 weeks = 16 exactly, so a perfect four weeks scores 100
// instead of 97).
//
// Every habit-relative "month" window uses this: the consistency score and
// period-completion bars here, and in ProfileTab the Overall Completion
// M1–M12 chart, the month trackers and the dot grid. The one deliberate
// exception is allowancePeriodDays' "month" below, which stays 30 because it
// backs a budget the user themselves labelled "per month".
export const HABIT_PERIOD_DAYS = 28;

// Returns the start date string of the rolling scoring period that contains
// `dateStr`. Periods are counted from the habit's start date.
// ── ALLOWANCE PERIODS (limited habits on a budget) ───────────────────────────
// The window a limited habit's attempt count is measured over, anchored to the
// habit's OWN start date — same rule every other habit visualization follows.
// (It used to reset on the calendar boundary, Monday or the 1st, which meant a
// habit started on a Thursday had its budget refill on a day unrelated to
// anything the user picked, and disagreed with the start-date-anchored dot row
// drawn right next to the count.)
//
// "week" is a rolling 7 days from the start date and "month" a rolling 30 —
// matching the periodLen the counted to-do habits already use.
export function allowancePeriodDays(habit) {
  return (habit.allowanceMode === "month" || habit.allowanceMode === "monthDates") ? 30 : 7;
}

// { start, end, reset } as YYYY-MM-DD strings, for the period containing
// dateStr. `reset` is the first day of the NEXT period — the day the count
// actually goes back to zero.
export function allowancePeriodBounds(habit, dateStr) {
  const origin = habit.habitStartDate || localDateStr(habit.createdAt) || dateStr;
  const len = allowancePeriodDays(habit);
  const originMs = new Date(origin + "T00:00:00").getTime();
  const targetMs = new Date(dateStr + "T00:00:00").getTime();
  // Dates before the habit even started fall in its first period, not a
  // negative one.
  const idx = Math.max(0, Math.floor((targetMs - originMs) / (len * 86400000)));
  const startMs = originMs + idx * len * 86400000;
  const endMs = startMs + (len - 1) * 86400000;
  return {
    start: fmt(new Date(startMs)),
    end: fmt(new Date(endMs)),
    reset: fmt(new Date(endMs + 86400000)),
    len,
  };
}

// How many attempts this habit has logged in the period containing dateStr,
// counting nothing later than dateStr itself.
export function allowanceUsed(habit, dateStr) {
  const { start } = allowancePeriodBounds(habit, dateStr);
  return (habit.attemptLog || []).filter(d => d >= start && d <= dateStr).length;
}

// Shared restricted-habit day color used by Week/Month/Year tracker views:
// only ever green (clean/within-allowance) or yellow (slipped/over-allowance),
// per Clarity's habit-color rule — never a third shade. Callers apply their
// own faded/neutral treatment for future or before-start days themselves.
export function restrictedDayColor(habit, dateStr, habitSkips = []) {
  if (habit.allowanceMode) {
    const max = habit.allowanceMax || 1;
    const cumulativeThroughDay = allowanceUsed(habit, dateStr);
    const usedOnDay = (habit.attemptLog || []).some(d => d === dateStr);
    const over = usedOnDay && cumulativeThroughDay > max;
    return over ? "#F5C842" : "#7DC87A";
  }
  const slipped = habit.relapseLog?.some(r => r.date === dateStr);
  if (slipped) return "#F5C842";
  const skipped = habitSkips.some(s => s.habitId === habit.id && s.skippedDate === dateStr);
  if (skipped) return "#e8e0f0";
  return "#7DC87A";
}

export function getPeriodStart(habit, dateStr) {
  const startBoundary = habit.habitStartDate || localDateStr(habit.createdAt) || "2000-01-01";
  const start = new Date(startBoundary + "T00:00:00");
  const target = new Date(dateStr + "T00:00:00");
  if (target < start) return startBoundary;
  const diffDays = Math.floor((target - start) / 86400000);
  const periodIndex = Math.floor(diffDays / HABIT_PERIOD_DAYS);
  const periodStart = new Date(start);
  periodStart.setDate(periodStart.getDate() + periodIndex * HABIT_PERIOD_DAYS);
  return fmt(periodStart);
}

// Returns { periodStart, periodEnd } strings for the rolling period containing dateStr.
export function getPeriodBounds(habit, dateStr) {
  const periodStart = getPeriodStart(habit, dateStr);
  const end = new Date(periodStart + "T00:00:00");
  end.setDate(end.getDate() + (HABIT_PERIOD_DAYS - 1));
  return { periodStart, periodEnd: fmt(end) };
}

// Formats a date string as "May 7" style label.
export function fmtPeriodLabel(dateStr) {
  const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const d = new Date(dateStr + "T00:00:00");
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
}

// ── BOUNDARIES (restricted habits) — honest-but-kind stats ───────────────────
// A day counts as "held" when it's clean OR a wobble (tempted but recovered).
// Only an explicit slip breaks a run. Slips are never hidden, but a slip resets
// only the CURRENT run — your BEST run is permanent and can't be erased.
export function isHabitWobbledOn(habit, key) {
  return (habit.wobbleLog || []).some(w => w.date === key);
}
export function getBoundaryStats(habit, habitSkips = []) {
  const startBoundary = habit.habitStartDate || localDateStr(habit.createdAt) || todayStr();
  const today = todayStr();
  const startMs = new Date(startBoundary + "T00:00:00").getTime();
  const todayMs = new Date(today + "T00:00:00").getTime();
  if (todayMs < startMs) return { current: 0, best: 0, cleanThisPeriod: 0, slipsThisPeriod: 0, lastSlip: null, totalSlips: 0, totalDays: 0 };

  const slipSet = new Set((habit.relapseLog || []).map(r => r.date));
  const skipSet = new Set((habitSkips || []).filter(s => s.habitId === habit.id).map(s => s.skippedDate));
  const { periodStart } = getPeriodBounds(habit, today);

  let current = 0, best = 0, run = 0;
  let cleanThisPeriod = 0, slipsThisPeriod = 0, totalSlips = 0, totalDays = 0;
  let lastSlip = null;

  for (let ms = startMs; ms <= todayMs; ms += 86400000) {
    const key = fmt(new Date(ms));
    if (skipSet.has(key)) continue; // skipped days are neutral — don't count or break
    totalDays++;
    const slipped = slipSet.has(key);
    if (slipped) {
      run = 0;
      totalSlips++;
      lastSlip = key;
      if (key >= periodStart) slipsThisPeriod++;
    } else {
      run++;
      if (run > best) best = run;
      if (key >= periodStart) cleanThisPeriod++;
    }
  }
  current = run;
  return { current, best, cleanThisPeriod, slipsThisPeriod, lastSlip, totalSlips, totalDays };
}


//
// DAILY habits:
//   score = sqrt(completionRate) × min(periodProgress × 2, 1) × 100
//   - periodProgress = daysElapsed / 30 (slow start based on calendar days)
//
// SPECIFIC / COUNT / MONTHLY habits:
//   score = sqrt(completionRate) × min((scheduledDaysElapsed / totalDueDays) × 2, 1) × 100
//   - slow start is based on how many of YOUR due days have passed, not 30 calendar days
//   - this makes the score feel fair regardless of how sparse your schedule is
//
// completionRate = completedDays / effectiveDue (skips count as 0.5 miss)
// Handles all habit types: todo, limited, nottodo
export function getPeriodScore(habit, habitLogs, periodStart, periodEnd, habitSkips = []) {
  // A period is judged by the goal in effect on its first day (goal changes
  // only ever start at a period boundary, so this is "the goal of its time").
  habit = goalOn(habit, periodStart);
  const today = todayStr();
  const skippedDates = habitSkips.filter(s => s.habitId === habit.id);
  const skippedDatesSet = new Set(skippedDates.map(s => s.skippedDate));
  const isNottodo = habit.habitType === "nottodo";
  const isLimited = habit.habitType === "limited";
  const isRestricted = isNottodo || isLimited;
  const isDaily = habit.frequencyType === "daily";
  const isCount = habit.frequencyType === "count";

  // ── RESTRICTED HABITS (nottodo / limited) ─────────────────
  // Same formula as todo habits — starts low, grows over time.
  // Default = clean (no action needed). Deductions:
  //   - slip (relapseLog entry)       → full miss (missedDays++)
  //   - skip without reschedule       → full miss (missedDays++)
  //   - skip with reschedule          → half miss (skippedDays++, worth 0.5)
  //   - everything else               → clean (completedDays++)
  if (isRestricted) {
    const todayDate = new Date(today + "T00:00:00");
    const startDate = new Date(periodStart + "T00:00:00");
    const daysElapsed = Math.min(Math.floor((todayDate - startDate) / 86400000) + 1, HABIT_PERIOD_DAYS);

    const slipDates = new Set((habit.relapseLog || [])
      .filter(r => r.date >= periodStart && r.date <= today && r.date <= periodEnd)
      .map(r => r.date));
    // Budget ("times per week/month") habits have no relapseLog: a day is a miss
    // when an attempt pushed the period past its allowance — the same rule that
    // turns the day's block yellow (restrictedDayColor).
    if (habit.allowanceMode) {
      const max = habit.allowanceMax || 1;
      for (const d of habit.attemptLog || []) {
        if (d < periodStart || d > today || d > periodEnd) continue;
        if (allowanceUsed(habit, d) > max) slipDates.add(d);
      }
    }
    const fullSkipDates = new Set();
    const halfSkipDates = new Set();
    for (const s of skippedDates) {
      if (s.skippedDate < periodStart || s.skippedDate > today || s.skippedDate > periodEnd) continue;
      if (s.rescheduledTo) halfSkipDates.add(s.skippedDate);
      else fullSkipDates.add(s.skippedDate);
    }

    let rCompletedDays = 0, rMissedDays = 0, rSkippedDays = 0, rExcludedDays = 0;
    for (let i = 0; i < daysElapsed; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const key = fmt(d);
      if (key > today || key > periodEnd) continue;
      // An excluded day is off: neither clean nor a slip, whatever was logged.
      if (isHabitExcludedOn(habit, key)) { rExcludedDays++; continue; }
      if (slipDates.has(key) || fullSkipDates.has(key)) rMissedDays++;
      else if (halfSkipDates.has(key)) rSkippedDays++;
      else rCompletedDays++;
    }

    const rEffectiveDue = rCompletedDays + rMissedDays + rSkippedDays * 0.5;
    const rTotalDays = daysElapsed - rExcludedDays;
    if (rEffectiveDue === 0) return { score: 0, completedDays: 0, totalDueDays: rTotalDays, daysElapsed };

    const rCompletionRate = rCompletedDays / rEffectiveDue;
    const periodProgress = daysElapsed / HABIT_PERIOD_DAYS;
    const rProgressMultiplier = Math.min(periodProgress * 2, 1);
    const score = Math.round(Math.sqrt(rCompletionRate) * rProgressMultiplier * 100);
    return { score, completedDays: rCompletedDays, totalDueDays: rTotalDays, daysElapsed };
  }

  let completedDays = 0;
  let missedDays = 0;
  let skippedDays = 0;
  let totalDueDays = 0;
  let scheduledDaysElapsed = 0; // due days that have already passed (for non-daily habits)

  const start = new Date(periodStart + "T00:00:00");

  const isMonthly = habit.frequencyType === "monthly";
  const todayDateObj = new Date(today + "T00:00:00");
  const calMonthStart = fmt(new Date(todayDateObj.getFullYear(), todayDateObj.getMonth(), 1));
  const calMonthEnd = fmt(new Date(todayDateObj.getFullYear(), todayDateObj.getMonth() + 1, 0));

  // ── Count total due days ──────────────────────────────────────────────────
  if (isCount) {
    // count habits: target is weeklyCount × (30/7)
    totalDueDays = Math.round((habit.weeklyCount || 1) * (HABIT_PERIOD_DAYS / 7));
  } else if (isMonthly) {
    if (habit.monthlyMode === "count") {
      totalDueDays = habit.monthlyCount || 1;
    } else {
      // monthly habits: total due days = number of set dates (e.g. [4,7,23,27] → 4),
      // minus any of this calendar month's dates that are excluded.
      const ym = calMonthStart.slice(0, 7);
      totalDueDays = (habit.monthDates || []).filter(n => !isHabitExcludedOn(habit, `${ym}-${String(n).padStart(2, "0")}`)).length;
    }
  } else {
    for (let i = 0; i < HABIT_PERIOD_DAYS; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const key = fmt(d);
      if (key > periodEnd) continue;
      if (isHabitDueOnKey(habit, key)) totalDueDays++;
    }
  }

  // ── Count completed / missed days ────────────────────────────────────────
  if (isMonthly && habit.monthlyMode === "count") {
    // monthly-count: count logged completions in the current calendar month
    for (let day = 1; day <= 31; day++) {
      const key = `${calMonthStart.slice(0, 7)}-${String(day).padStart(2, "0")}`;
      if (key > today) continue;
      if (key > calMonthEnd) continue;
      if (habitLogs[key]?.[habit.id]) completedDays++;
    }
    scheduledDaysElapsed = habit.monthlyCount || 1;
  } else if (isMonthly) {
    // monthly habits: use current calendar month as window (not rolling 30-day period)
    // so dates like the 4th and 7th are visible even if habit was created mid-month
    const monthDates = habit.monthDates || [];
    for (const dateNum of monthDates) {
      const key = `${calMonthStart.slice(0, 7)}-${String(dateNum).padStart(2, "0")}`;
      if (key > today) continue; // future date this month
      if (key > calMonthEnd) continue; // invalid date (e.g. 31st in a 30-day month)
      if (isHabitExcludedOn(habit, key)) continue; // a day off — not due
      scheduledDaysElapsed++;
      if (skippedDatesSet.has(key)) {
        skippedDays++;
      } else if (isHabitDoneOn(habit, habitLogs, key)) {
        completedDays++;
      } else {
        missedDays++;
      }
    }
  } else {
    for (let i = 0; i < HABIT_PERIOD_DAYS; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const key = fmt(d);
      if (key > today) continue;
      if (key > periodEnd) continue;

      if (isCount) {
        // count habits: track logged days; scheduledDaysElapsed computed after loop
        if (habitLogs[key]?.[habit.id]) completedDays++;
        continue;
      }

      const isDue = isHabitDueOnKey(habit, key);
      if (!isDue) continue;

      // track elapsed due days for non-daily slow-start
      if (!isDaily) scheduledDaysElapsed++;

      if (skippedDatesSet.has(key)) {
        skippedDays++;
      } else if (isNottodo) {
        // nottodo: clean = no slip logged that day
        const slipped = habit.relapseLog?.some(r => r.date === key);
        if (slipped) missedDays++; else completedDays++;
      } else if (isLimited) {
        // limited: check relapseLog for explicit slips
        const slipped = habit.relapseLog?.some(r => r.date === key);
        if (slipped) {
          missedDays++;
        } else {
          const logVal = habitLogs[key]?.[habit.id];
          const instances = typeof logVal === "number" ? logVal : (logVal ? 1 : 0);
          const overLimit = instances > (habit.maxTimesPerDay || 1);
          if (overLimit) missedDays++; else completedDays++;
        }
      } else {
        // regular todo habit
        if (isHabitDoneOn(habit, habitLogs, key)) completedDays++;
        else missedDays++;
      }
    }

    // count habits: scheduledDaysElapsed = max(completedDays, weeklyCount × (calendarDaysElapsed / 7))
    if (isCount) {
      const todayMs = new Date(today + "T00:00:00").getTime();
      const startMs = start.getTime();
      const calendarDaysElapsed = Math.min(Math.floor((todayMs - startMs) / 86400000) + 1, HABIT_PERIOD_DAYS);
      const timeBasedElapsed = Math.round((habit.weeklyCount || 1) * (calendarDaysElapsed / 7));
      scheduledDaysElapsed = Math.max(completedDays, timeBasedElapsed);
      missedDays = Math.max(0, scheduledDaysElapsed - completedDays);
    }
  }

  const todayDate = new Date(today + "T00:00:00");
  const startDate = new Date(periodStart + "T00:00:00");
  const daysElapsed = Math.min(Math.floor((todayDate - startDate) / 86400000) + 1, HABIT_PERIOD_DAYS);

  const effectiveDue = completedDays + missedDays + skippedDays * 0.5;
  if (effectiveDue === 0) return { score: 0, completedDays: 0, totalDueDays, daysElapsed };

  // monthly: completionRate against full target (e.g. 3/4 = 75%, not 3/3 = 100%)
  // specific/count/daily: completionRate against elapsed dates only
  const completionRate = isMonthly
    ? completedDays / totalDueDays
    : completedDays / effectiveDue;

  let progressMultiplier;
  if (isDaily) {
    // daily: slow start based on calendar days (original formula)
    const periodProgress = daysElapsed / HABIT_PERIOD_DAYS;
    progressMultiplier = Math.min(periodProgress * 2, 1);
  } else {
    // specific/count/monthly: slow start based on YOUR scheduled due days elapsed
    const scheduledProgress = totalDueDays > 0 ? scheduledDaysElapsed / totalDueDays : 0;
    progressMultiplier = Math.min(scheduledProgress * 2, 1);
  }

  // monthly: raw completion rate (no sqrt mercy curve — few dates so each one counts exactly)
  // specific/count/daily: sqrt mercy curve softens the impact of misses
  const score = Math.round((isMonthly ? completionRate : Math.sqrt(completionRate)) * progressMultiplier * 100);

  return { score, completedDays, totalDueDays, daysElapsed };
}

// Returns current period's score -- used for the big % on the habit card
export function getConsistencyScore(habit, habitLogs, habitSkips = []) {
  const today = todayStr();
  const { periodStart, periodEnd } = getPeriodBounds(habit, today);
  const { score, completedDays, totalDueDays } = getPeriodScore(habit, habitLogs, periodStart, periodEnd, habitSkips);
  return { score, cleanDays: completedDays, totalDays: totalDueDays };
}

// Returns last N periods of scores for the breakdown in the stats modal
export function getMonthlyBreakdown(habit, habitLogs, monthsBack = 6, habitSkips = []) {
  const today = todayStr();
  const result = [];
  // Walk backwards period by period from current
  let checkDate = today;
  for (let i = 0; i < monthsBack; i++) {
    const { periodStart, periodEnd } = getPeriodBounds(habit, checkDate);
    const { score, completedDays, totalDueDays } = getPeriodScore(habit, habitLogs, periodStart, periodEnd, habitSkips);
    const label = `${fmtPeriodLabel(periodStart)} – ${fmtPeriodLabel(periodEnd)}`;
    result.unshift({ label, score, completedDays, pastDueDays: totalDueDays, totalDueDays, periodStart, periodEnd });
    // Move to day before this period started
    const prev = new Date(periodStart + "T00:00:00");
    prev.setDate(prev.getDate() - 1);
    checkDate = fmt(prev);
    if (checkDate < (habit.habitStartDate || habit.createdAt || "2000-01-01")) break;
  }
  return result;
}

// ─── UPCOMING HABIT REMINDERS (Android AlarmManager sync) ────────────────────
// Same shape/idea as taskLogic's collectUpcomingAlarms/collectUpcomingNotifications
// — "what will fire in the next `lookaheadDays`?" — but for habits, which have
// no due timestamp to offset from. Instead each habit optionally carries a
// single `reminderTime` ("HH:MM"); on any day it's due (per
// isHabitDueForReminderOn — which for counted habits means the single day the
// reminder was armed for) and not already completed, it fires once at that
// time. Restricted habits
// (nottodo/limited) are about resisting, not "doing", so they're excluded —
// only "todo" habits get reminders for now.
function _collectUpcomingHabitByAlertType(wantAlarm, { habits = [], habitLogs = {}, habitSkips = [], lookaheadDays = 35, maxCount = 300, fromDate = new Date() }) {
  const upcoming = [];
  const fromMs = fromDate.getTime();
  const untilMs = fromMs + lookaheadDays * 86400000;
  const fromDateStr = fmt(fromDate);

  habits.forEach(h => {
    if (h.habitType !== "todo") return;
    if (!h.reminderTime) return;
    const isAlarm = h.reminderType === "alarm";
    if (isAlarm !== wantAlarm) return;
    for (let dayOffset = 0; dayOffset <= lookaheadDays; dayOffset++) {
      const dateStr = addDaysToDateStr(fromDateStr, dayOffset);
      if (!isWithinHabitRange(h, dateStr)) continue;
      if (!isHabitDueForReminderOn(h, dateStr)) continue;
      if (isHabitDoneOn(h, habitLogs, dateStr, habitSkips)) continue;
      const anchor = dateTimeToDate(dateStr, h.reminderTime).getTime();
      const offsets = Array.isArray(h.reminderOffsets) && h.reminderOffsets.length ? h.reminderOffsets : [0];
      for (const off of offsets) {
        const triggerAt = anchor - (Number(off) || 0) * 60000;
        if (triggerAt < fromMs || triggerAt > untilMs) continue;
        upcoming.push({
          key: `habit-${h.id}:${triggerAt}`,
          title: h.name || "(untitled habit)",
          body: "Habit reminder",
          triggerAt,
          alertType: isAlarm ? "alarm" : "notification",
          alarmSound: h.alarmSound || DEFAULT_ALARM_SOUND,
          alarmSoundUri: h.alarmSoundUri || undefined,
        });
      }
    }
  });
  upcoming.sort((a, b) => a.triggerAt - b.triggerAt);
  return upcoming.slice(0, maxCount);
}

export function collectUpcomingHabitAlarms(opts) {
  return _collectUpcomingHabitByAlertType(true, opts);
}

export function collectUpcomingHabitNotifications(opts) {
  return _collectUpcomingHabitByAlertType(false, opts);
}

// ─── DUE HABIT REMINDERS (foreground/web poller — mirrors taskLogic's
// collectDueReminders) ── "is a habit's reminder due right now?", for the
// in-app poller that toasts (tab visible) or asks the service worker to
// show a notification (tab hidden, web only — native owns its own via
// collectUpcomingHabitAlarms/Notifications above).
export function collectDueHabitReminders({ habits = [], habitLogs = {}, habitSkips = [], firedMap = {}, nowDate = new Date() }) {
  const due = [];
  const todayD = todayStr();
  habits.forEach(h => {
    if (h.habitType !== "todo" || !h.reminderTime) return;
    for (const dayOffset of [0, -1]) { // today, and yesterday (in case "now" just rolled past midnight before the poller caught it)
      const dateStr = addDaysToDateStr(todayD, dayOffset);
      if (!isWithinHabitRange(h, dateStr)) continue;
      if (!isHabitDueOn(h, dateStr)) continue;
      if (isHabitDoneOn(h, habitLogs, dateStr, habitSkips)) continue;
      const anchor = dateTimeToDate(dateStr, h.reminderTime).getTime();
      const offsets = Array.isArray(h.reminderOffsets) && h.reminderOffsets.length ? h.reminderOffsets : [0];
      for (const off of offsets) {
        const targetMs = anchor - (Number(off) || 0) * 60000;
        const key = `habit-${h.id}:${targetMs}`;
        if (firedMap[key]) continue;
        const deltaMs = nowDate.getTime() - targetMs;
        if (deltaMs >= 0 && deltaMs <= NOTIF_GRACE_MS) {
          due.push({
            key, itemType: "habit", itemId: h.id,
            title: h.name || "(untitled habit)",
            body: "Habit reminder",
            alertType: h.reminderType === "alarm" ? "alarm" : "notification",
            alarmSound: h.alarmSound || DEFAULT_ALARM_SOUND,
            alarmSoundUri: h.alarmSoundUri || undefined,
            deltaMs,
          });
        }
      }
    }
  });
  return due;
}

